import {
    Button,
    Flex,
    Select,
    SelectItem,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeaderCell,
    TableRow,
    Text,
    TextInput,
    Title,
} from '@tremor/react'
import { useEffect, useState } from 'react'
import {
    ArrowPathIcon,
    PencilSquareIcon,
    TrashIcon,
} from '@heroicons/react/24/outline'
import { useLocation } from 'react-router-dom'
import {
    useApiV1RoleAssignmentCreate,
    useApiV1RoleAssignmentDelete,
    useApiV1RoleAssignmentList,
} from '../../api/api.gen'
import Modal from '../../components/Layout/modal'
import { toErrorString, toErrorText } from '../../utilities/error'
import { ModelAccess } from '../../api/api'
import Confirm from '../../components/Layout/confirm'

export default function RoleAssignmentPage() {
    const location = useLocation()
    const [deleteID, setDeleteID] = useState<string>()
    const [showModal, setShowModal] = useState(false)
    const [email, setEmail] = useState('')
    const [access, setAccess] = useState('')
    const {
        isLoading: assignRoleLoading,
        isExecuted: assignRoleExecuted,
        response: assignRoleResponse,
        error: assignRoleError,
        sendNowWithParams: assignRole,
    } = useApiV1RoleAssignmentCreate({}, {}, false)

    const {
        isLoading: deleteLoading,
        isExecuted: deleteExecuted,
        sendNowWithParams: deleteKey,
        error: deleteError,
    } = useApiV1RoleAssignmentDelete({ user_id: '' }, {}, false)

    const { response: roleList, sendNow: refresh } =
        useApiV1RoleAssignmentList()

    useEffect(() => {
        if (
            assignRoleExecuted &&
            !assignRoleLoading &&
            toErrorString(assignRoleError).length === 0
        ) {
            setEmail('')
            setAccess('')
            setShowModal(false)
            refresh()
        }
    }, [assignRoleLoading])

    useEffect(() => {
        if (
            deleteExecuted &&
            !deleteLoading &&
            toErrorString(deleteError).length === 0
        ) {
            setDeleteID(undefined)
            refresh()
        }
    }, [deleteLoading])

    useEffect(() => {
        if (location.hash === '#create') {
            setShowModal(true)
        }
    }, [])

    return (
        <Flex
            className="p-4 bg-gray-50 w-auto min-w-[1000px] max-w-fit mt-4 rounded-md"
            flexDirection="col"
            justifyContent="start"
            alignItems="start"
        >
            <Flex flexDirection="row" justifyContent="between">
                <Title className="text-teal-600">Role assignments</Title>
                <Flex className="w-fit space-x-2">
                    <Button
                        icon={ArrowPathIcon}
                        variant="secondary"
                        color="teal"
                        disabled={assignRoleExecuted && assignRoleLoading}
                        onClick={() => refresh()}
                    />
                    <Button
                        variant="secondary"
                        color="teal"
                        onClick={() => setShowModal(true)}
                    >
                        New role assignment
                    </Button>
                </Flex>
            </Flex>
            <Table className="w-full">
                <TableHead>
                    <TableRow>
                        <TableHeaderCell>Email</TableHeaderCell>
                        <TableHeaderCell>Access</TableHeaderCell>
                        <TableHeaderCell>Actions</TableHeaderCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {roleList?.map((v) => (
                        <TableRow>
                            <TableCell>{v.user_id}</TableCell>
                            <TableCell>{v.access}</TableCell>
                            <TableCell>
                                <Flex
                                    flexDirection="row"
                                    justifyContent="start"
                                    className="space-x-2"
                                >
                                    <TrashIcon
                                        onClick={() => setDeleteID(v.user_id)}
                                        className="w-4 h-4 text-teal-500 cursor-pointer"
                                    />
                                    <PencilSquareIcon
                                        onClick={() => {
                                            setEmail(v.user_id || '')
                                            setAccess(v.access || '')
                                            setShowModal(true)
                                        }}
                                        className="w-4 h-4 text-teal-500 cursor-pointer"
                                    />
                                </Flex>
                            </TableCell>
                        </TableRow>
                    ))}
                    {(roleList?.length || 0) === 0 && (
                        <TableRow>
                            <TableCell colSpan={6}>
                                <Text className="w-full text-gray-400 text-center font-normal">
                                    {assignRoleExecuted && assignRoleLoading
                                        ? 'Loading'
                                        : 'Empty list'}
                                </Text>
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>

            <Confirm
                title={`Are you sure you want to delete ${roleList
                    ?.filter((v) => v.user_id === deleteID)
                    .map((v) => v.user_id)}?`}
                show={deleteID !== undefined}
                loading={deleteExecuted && deleteLoading}
                error={deleteError}
                onCanceled={() => setDeleteID(undefined)}
                onConfirmed={() => deleteKey({ user_id: deleteID || '' }, {})}
            />

            <Modal
                title="Add a new role assignment"
                show={showModal}
                onCloseClicked={() => {
                    setEmail('')
                    setAccess('')
                    setShowModal(false)
                }}
            >
                <Text className="mt-4 mb-1">Email:</Text>
                <TextInput
                    value={email}
                    onValueChange={setEmail}
                    error={email.length === 0}
                    errorMessage="Email is required"
                />

                <Text className="mt-4 mb-1">Access:</Text>
                <Select
                    className="h-60"
                    value={access}
                    onValueChange={setAccess}
                >
                    <SelectItem value="viewer">Viewer</SelectItem>
                    <SelectItem value="editor">Editor</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                </Select>
                {toErrorText(assignRoleError)}
                <Button
                    className="mt-8"
                    color="teal"
                    loading={assignRoleLoading && assignRoleExecuted}
                    disabled={assignRoleLoading && assignRoleExecuted}
                    onClick={() => {
                        assignRole(
                            {
                                user_id: email,
                                access: access as ModelAccess,
                            },
                            {}
                        )
                    }}
                >
                    Add
                </Button>
            </Modal>
        </Flex>
    )
}
